/**
 * compliance.ts - Moduł sprawdzania zgodności z normami
 * PlexiSystem Engineering Module
 */

import { MaterialKey } from '../constants/materials';
import { 
  UseCase,
  STANDARDS,
  checkCompliance,
  getRequiredTests,
  generateComplianceDeclaration,
  checkMaterialForUseCase
} from '../constants/standards';

export interface ComplianceInput {
  use_case: UseCase;
  material: MaterialKey;
  thickness_mm: number;
  edges_radius_mm: number;
  uv_stabilized: boolean;
  food_contact: boolean;
  certificates?: string[];
}

export interface ComplianceResult {
  compliant: boolean;
  requirements_met: Array<{
    requirement: string;
    status: 'pass' | 'fail' | 'warning';
    details: string;
  }>;
  required_tests: string[];
  required_certificates: string[];
  declaration_text: string;
  recommendations: string[];
  warnings: string[];
}

/**
 * Główna funkcja sprawdzania zgodności
 */
export function checkComplianceModule(input: ComplianceInput): ComplianceResult {
  const result: ComplianceResult = {
    compliant: true,
    requirements_met: [],
    required_tests: [],
    required_certificates: [],
    declaration_text: '',
    recommendations: [],
    warnings: []
  };
  
  // Sprawdź kompatybilność materiału
  const materialCheck = checkMaterialForUseCase(input.material, input.use_case);
  if (!materialCheck.suitable) {
    result.compliant = false;
    result.warnings.push(materialCheck.reason || 'Materiał nieodpowiedni');
  }
  
  // Sprawdź zgodność z normami
  const compliance = checkCompliance(input.use_case, {
    material: input.material,
    thickness_mm: input.thickness_mm,
    edge_radius_mm: input.edges_radius_mm,
    uv_stabilized: input.uv_stabilized,
    certificates: input.certificates
  });
  
  if (!compliance.compliant) {
    result.compliant = false;
    result.warnings.push(...compliance.missing);
  }
  
  result.warnings.push(...compliance.warnings);
  
  // Sprawdź szczegółowe wymagania
  const standard = STANDARDS[input.use_case];
  
  // Sprawdzenie grubości
  if (standard.minima?.thickness_PC && input.material === 'PC') {
    const range = standard.minima.thickness_PC.match(/(\d+)-(\d+)/);
    if (range) {
      const [, min, max] = range;
      const status = input.thickness_mm >= parseInt(min) && input.thickness_mm <= parseInt(max);
      result.requirements_met.push({
        requirement: `Grubość PC: ${standard.minima.thickness_PC}`,
        status: status ? 'pass' : 'fail',
        details: `Aktualna: ${input.thickness_mm}mm`
      });
    }
  }
  
  // Sprawdzenie promienia krawędzi
  if (standard.minima?.edge_radius) {
    const minRadius = parseInt(standard.minima.edge_radius.match(/≥(\d+)/)?.[1] || '0');
    const status = input.edges_radius_mm >= minRadius;
    result.requirements_met.push({
      requirement: `Promień krawędzi ${standard.minima.edge_radius}`,
      status: status ? 'pass' : 'fail',
      details: `Aktualny: ${input.edges_radius_mm}mm`
    });
    
    if (!status) {
      result.compliant = false;
      result.recommendations.push(`Zwiększ promień krawędzi do minimum ${minRadius}mm`);
    }
  }
  
  // Sprawdzenie kontaktu z żywnością
  if (input.use_case === 'food_contact') {
    if (!input.food_contact) {
      result.compliant = false;
      result.requirements_met.push({
        requirement: 'Atest PZH/FDA',
        status: 'fail',
        details: 'Brak atestu do kontaktu z żywnością'
      });
      result.recommendations.push('Uzyskaj atest PZH lub FDA dla materiału');
    } else {
      result.requirements_met.push({
        requirement: 'Atest PZH/FDA',
        status: 'pass',
        details: 'Materiał dopuszczony do kontaktu z żywnością'
      });
    }
  }
  
  // Sprawdzenie stabilizacji UV
  if (input.use_case === 'uv_heat_resistance' || input.use_case === 'pos_display') {
    if (!input.uv_stabilized && !['PC', 'PMMA'].includes(input.material)) {
      result.requirements_met.push({
        requirement: 'Stabilizacja UV',
        status: 'warning',
        details: 'Materiał wymaga stabilizacji UV lub powłoki'
      });
      result.recommendations.push('Zastosuj stabilizatory UV lub powłokę ochronną');
    }
  }
  
  // Pobierz wymagane testy
  result.required_tests = getRequiredTests(input.use_case);
  
  // Pobierz wymagane certyfikaty
  result.required_certificates = standard.certificates || [];
  
  // Generuj deklarację zgodności
  result.declaration_text = generateComplianceDeclaration(input.use_case, {
    name: `Element ${input.use_case.replace(/_/g, ' ')}`,
    material: input.material,
    thickness_mm: input.thickness_mm,
    manufacturer: 'PlexiSystem Sp. z o.o.',
    date: new Date()
  });
  
  // Dodatkowe zalecenia
  if (input.use_case === 'machine_guard') {
    result.recommendations.push('Przeprowadź test udarności zgodnie z PN-EN ISO 179-1');
    result.recommendations.push('Zapewnij mocowanie uniemożliwiające demontaż bez narzędzi');
  }
  
  if (input.use_case === 'medical') {
    result.recommendations.push('Wykonaj testy biokompatybilności wg ISO 10993');
    result.recommendations.push('Zapewnij możliwość sterylizacji');
  }
  
  if (input.use_case === 'cleanroom') {
    result.recommendations.push('Wykonaj test emisji cząstek');
    result.recommendations.push('Sprawdź właściwości antystatyczne');
  }
  
  return result;
}

/**
 * Generuje PDF z deklaracją zgodności
 */
export function generateCompliancePDF(result: ComplianceResult): string {
  // To będzie generowane przez UI component
  return result.declaration_text;
}

/**
 * Sprawdza wymagania BHP
 */
export function checkSafetyRequirements(
  use_case: UseCase,
  parameters: {
    edge_protection?: boolean;
    warning_labels?: boolean;
    emergency_stop?: boolean;
    interlock?: boolean;
  }
): {
  compliant: boolean;
  missing: string[];
} {
  const missing: string[] = [];
  
  if (use_case === 'machine_guard') {
    if (!parameters.edge_protection) {
      missing.push('Zabezpieczenie krawędzi');
    }
    if (!parameters.warning_labels) {
      missing.push('Oznakowanie ostrzegawcze');
    }
    if (!parameters.emergency_stop) {
      missing.push('Wyłącznik bezpieczeństwa w zasięgu');
    }
    if (!parameters.interlock) {
      missing.push('Blokada przy otwartej osłonie');
    }
  }
  
  return {
    compliant: missing.length === 0,
    missing
  };
}
